import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getQueryFn, apiRequest } from '../../lib/queryClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import type { Country } from '../../types';

interface Props {
  companyId: number | null | undefined;
  onSaved: () => void;
}

interface VehicleEconomyRow {
  vehicle_type: string;
  min_kmpl: number;
  max_kmpl: number;
  notes?: string;
}

interface RowState {
  vehicle_type: string;
  vehicles_count: number;
  annual_distance_km: number;
  fuel_economy_kmpl?: number;
}

export function Scope1Scenario1Form({ companyId, onSaved }: Props) {
  const { data: economy } = useQuery<VehicleEconomyRow[]>({
    queryKey: ['/api/ghg/scope1/vehicle-economy'],
    queryFn: getQueryFn(),
  });

  const { data: companyCountries } = useQuery<Country[]>({
    queryKey: companyId ? [`/api/countries/company/${companyId}`] : [null],
    queryFn: getQueryFn(),
    enabled: !!companyId,
  });

  const [fuelType, setFuelType] = useState<'Diesel' | 'Petrol'>('Diesel');
  const [country, setCountry] = useState('');
  const [rows, setRows] = useState<RowState[]>([
    { vehicle_type: 'SUV', vehicles_count: 1, annual_distance_km: 10000 },
  ]);
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');

  const midpoint = (v?: VehicleEconomyRow) => v ? (v.min_kmpl + v.max_kmpl) / 2 : undefined;

  const computed = useMemo(() => {
    const lookup = new Map((economy || []).map(e => [e.vehicle_type, e]));
    let totalLiters = 0;
    for (const r of rows) {
      const ref = lookup.get(r.vehicle_type);
      const kmpl = r.fuel_economy_kmpl && r.fuel_economy_kmpl > 0 ? r.fuel_economy_kmpl : midpoint(ref) || 0;
      if (kmpl > 0) totalLiters += r.vehicles_count * (r.annual_distance_km / kmpl);
    }
    return { totalLiters };
  }, [rows, economy]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error('Select a company first');
      const payload = {
        company_id: companyId,
        fuel_type: fuelType,
        country_code: country || undefined,
        rows: rows.map(r => ({
          vehicle_type: r.vehicle_type,
          vehicles_count: Number(r.vehicles_count || 0),
          annual_distance_km: Number(r.annual_distance_km || 0),
          fuel_economy_kmpl: r.fuel_economy_kmpl ? Number(r.fuel_economy_kmpl) : undefined,
        })),
        reporting_year: reportingYear,
        notes: notes || undefined,
      };
      const res = await apiRequest('POST', '/api/ghg/scope1/scenario1', payload);
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: () => onSaved(),
  });

  const updateRow = (idx: number, patch: Partial<RowState>) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Country"
          value={country}
          options={[
            { value: '', label: 'Select country…' },
            ...(companyCountries || []).map((c: Country) => ({ value: c.code, label: c.name }))
          ]}
          onChange={(e: any) => setCountry(e?.target ? e.target.value : e)}
        />
        <div>
          <label className="block text-sm text-gray-600 mb-1">Fuel type</label>
          <select className="w-full border rounded px-3 py-2" value={fuelType} onChange={(e) => setFuelType(e.target.value as any)}>
            <option>Diesel</option>
            <option>Petrol</option>
          </select>
        </div>
        <Input
          label="Reporting Year"
          type="number"
          min="2000"
          max="2100"
          value={reportingYear}
          onChange={(e) => setReportingYear(parseInt(e.target.value) || new Date().getFullYear())}
          required
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600">
              <th className="py-2 pr-4">Vehicle type</th>
              <th className="py-2 pr-4">Count</th>
              <th className="py-2 pr-4">Distance / vehicle (km)</th>
              <th className="py-2 pr-4">Fuel economy (km/L)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const ref = (economy || []).find(e => e.vehicle_type === r.vehicle_type);
              const placeholder = ref ? `${ref.min_kmpl}–${ref.max_kmpl}` : '';
              return (
                <tr key={idx} className="border-t">
                  <td className="py-2 pr-4">
                    <select className="border rounded px-2 py-1 w-48" value={r.vehicle_type} onChange={(e) => updateRow(idx, { vehicle_type: e.target.value })}>
                      {(economy || []).map(v => (
                        <option key={v.vehicle_type} value={v.vehicle_type}>{v.vehicle_type}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <input className="border rounded px-2 py-1 w-24" type="number" min={0} value={r.vehicles_count}
                      onChange={(e) => updateRow(idx, { vehicles_count: Number(e.target.value) })} />
                  </td>
                  <td className="py-2 pr-4">
                    <input className="border rounded px-2 py-1 w-28" type="number" min={0} value={r.annual_distance_km}
                      onChange={(e) => updateRow(idx, { annual_distance_km: Number(e.target.value) })} />
                  </td>
                  <td className="py-2 pr-4">
                    <input className="border rounded px-2 py-1 w-28" type="number" min={0} value={r.fuel_economy_kmpl ?? ''} placeholder={placeholder}
                      onChange={(e) => updateRow(idx, { fuel_economy_kmpl: e.target.value ? Number(e.target.value) : undefined })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setRows(prev => [...prev, { vehicle_type: economy?.[0]?.vehicle_type || 'SUV', vehicles_count: 1, annual_distance_km: 10000 }])}>Add row</Button>
        {rows.length > 1 && (
          <Button variant="outline" onClick={() => setRows(prev => prev.slice(0, -1))}>Remove last</Button>
        )}
        <div className="ml-auto">
          <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
            Estimated fuel: {computed.totalLiters.toFixed(2)} L
          </div>
        </div>
      </div>

      <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !companyId}>
        {mutation.isPending ? 'Saving...' : 'Save Estimation'}
      </Button>
    </div>
  );
}
